/**
 * Menu add-ons ("Extra cheese", "Bacon") and the two shapes one travels in.
 *
 * Mirrors iOS `MenuSupplement` / `LineItemSupplement`. The menu payload and the order payload
 * disagree about how an add-on is spelled — the menu sends the catalogue entry (string price,
 * localized meta rows), the cart sends a picked one (numeric price, a quantity) — so the two are
 * separate types rather than one optional-everything blob that every call site has to re-check.
 */

/**
 * An add-on as it arrives on the menu payload (`api/v4/shops/{id}/menu`).
 *
 * Present only for Hype venues that have add-ons switched on; absent or empty everywhere else, so
 * a missing array means "this product has no add-ons" rather than an error. Every field is
 * optional for the same reason the rest of the menu models are: a venue that never enabled the
 * feature must not produce a decode failure on a menu it never asked for.
 */
export interface MenuSupplement {
  id?: number;
  name?: string;
  /** Per unit of the PRODUCT, as a string — the same money spelling the menu uses for products. */
  price?: string;
  /** Localized names, one row per language, keyed exactly as a product's own name rows are. */
  meta_data?: Array<{ key: string; value: string }>;
}

/**
 * An add-on as it sits on a cart line and on the outbound order payload.
 *
 * `price` is per unit of the PRODUCT, not per line: a pizza with "+50 cheese" ordered twice costs
 * two base prices plus two times fifty. `quantity` is the add-on's own multiplier ("double
 * cheese") and is at least 1 — a zero would silently vanish from the money while still changing
 * the line's identity.
 */
export interface CartSupplement {
  id: number;
  /**
   * Snapshot of the localized name taken when the guest ticked the box. Only a fallback for
   * display: the catalogue holds the localized meta rows, so a name that must follow a language
   * switch is re-resolved through `SupplementCatalogService.localizedSupplementName`.
   */
  name?: string;
  price: number;
  quantity: number;
}

/**
 * An add-on as it may arrive back from the wire.
 *
 * The server sends numbers, but a client that wrote strings must still round-trip: line items read
 * back from `GET /orders` and carts written by an older build both land here. iOS decodes exactly
 * this way (`LineItemSupplement.init(from:)`) — anything else turns one bad row into a NaN that
 * poisons the whole cart total.
 */
export interface RawCartSupplement {
  id?: number | string | null;
  name?: string | null;
  price?: number | string | null;
  quantity?: number | string | null;
}

/** The three keys the order payload carries for an add-on. */
export interface SupplementPayload {
  id: number;
  price: number;
  quantity: number;
}

/**
 * The cart's representation of picking this catalogue add-on once.
 *
 * `name` is passed in rather than read off `supplement.name` when the caller has already localized
 * it — this module stays free of Angular so the pricing/identity rules can be unit-tested and
 * imported from anywhere, and localization needs `LanguageService`.
 */
export function toCartSupplement(supplement: MenuSupplement, name?: string): CartSupplement {
  return {
    id: Math.trunc(toNumber(supplement.id)),
    name: name ?? supplement.name,
    price: toNumber(supplement.price),
    quantity: 1
  };
}

/**
 * Coerce one add-on off the wire into the cart's shape.
 *
 * An unparseable id becomes 0 rather than being rejected, matching iOS: dropping the entry would
 * change the line's identity behind the guest's back and split one order line into two.
 */
export function normalizeCartSupplement(raw: RawCartSupplement): CartSupplement {
  const quantity = Math.trunc(toNumber(raw.quantity));
  return {
    id: Math.trunc(toNumber(raw.id)),
    name: raw.name ?? undefined,
    price: toNumber(raw.price),
    quantity: quantity >= 1 ? quantity : 1
  };
}

/**
 * Coerce a whole selection, collapsing "none" to `undefined`.
 *
 * Empty and absent are the same thing everywhere else in this feature (a WooCommerce line has no
 * add-ons; so does a Hype line the guest left plain), so they are made the same value here — that
 * is what lets the payload builder omit the field on a single `length > 0` check instead of every
 * call site remembering to.
 */
export function normalizeCartSupplements(
  raw: RawCartSupplement[] | null | undefined
): CartSupplement[] | undefined {
  if (!raw || raw.length === 0) return undefined;
  const normalized = raw
    .filter((entry): entry is RawCartSupplement => !!entry)
    .map(entry => normalizeCartSupplement(entry));
  return normalized.length > 0 ? normalized : undefined;
}

/**
 * The shape the order payload carries.
 *
 * The backend re-checks every id against the catalogue and re-prices from it, so this is a
 * request, not a source of truth — `name` is deliberately not sent.
 */
export function supplementPayload(supplement: CartSupplement): SupplementPayload {
  return {
    id: supplement.id,
    price: supplement.price,
    quantity: supplement.quantity
  };
}

function toNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') return isFinite(value) ? value : 0;
  const parsed = parseFloat(String(value ?? ''));
  return isFinite(parsed) ? parsed : 0;
}
